/**
 * `/census/:token` — la liste d'employés remplie par le client (chunk 9.BK.1).
 *
 * ## Ce que cet écran promet
 *
 * **La reprise sans perte.** Il n'y a pas de brouillon local à restaurer : le
 * census EST l'état persistant côté serveur. Fermer l'onglet et revenir le
 * lendemain rend la liste telle quelle. Chaque enregistrement est **annoncé** —
 * « Enregistrement… », « Enregistré », ou une erreur qui garde la valeur à
 * l'écran plutôt que de la faire disparaître.
 *
 * **Aucune règle métier recalculée ici.** Les manques affichés, ligne par ligne
 * et champ par champ, viennent du serveur. Une validation réécrite dans le
 * navigateur finirait par diverger de celle qui refuse réellement.
 *
 * **« J'ai terminé » n'est pas un verrou.** Il fait passer la liste en
 * `submitted` : le client a fini, le conseiller révise. Le verrou appartient au
 * cabinet, et l'écran le dit.
 *
 * **Un verrou posé pendant la saisie ne donne pas une page morte.** Si le
 * conseiller verrouille alors que la page est ouverte, la prochaine lecture rend
 * `locked` et l'écran affiche « cette liste est finalisée » — pas « lien
 * indisponible ». Les écritures, elles, sont refusées uniformément.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import { appliquerLangueEphemere } from "../../lib/localeEphemere";
import {
  usePublicCensus,
  usePublicCensusMutations,
  type CensusIssue,
  type CensusParticipantRow,
} from "../../lib/queries/census";
import { IntakeChrome } from "../public-intake/_parts/IntakeChrome";

import { CensusWorkbookPanel } from "./_parts/CensusWorkbookPanel";
import { CHAMPS_DEPENDANT, CHAMPS_PARTICIPANT, versLeFil } from "./_parts/champs";
import { FicheEmploye, type EtatSauvegarde } from "./_parts/FicheEmploye";

/** Un identifiant de ligne, choisi par le client. Idempotence, pas périmètre. */
const nouvelId = (): string =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`.replace(/\D/g, "").slice(0, 32);

type Brouillon = Record<string, Record<string, string>>;

const enChaines = (r: Record<string, unknown>): Record<string, string> => {
  const s: Record<string, string> = {};
  for (const [k, v] of Object.entries(r)) s[k] = v === null || v === undefined ? "" : String(v);
  return s;
};

export function PublicCensusPage(): JSX.Element {
  const { token = "" } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();

  const requete = usePublicCensus(token);
  const m = usePublicCensusMutations(token);

  const langue = searchParams.get("lang") === "en" ? "en" : "fr";
  useEffect(() => {
    void appliquerLangueEphemere(i18n, langue);
  }, [i18n, langue]);

  const [local, setLocal] = useState<Brouillon>({});
  const [etats, setEtats] = useState<Record<string, EtatSauvegarde>>({});
  const [ajoutees, setAjoutees] = useState<string[]>([]);
  const [depAjoutees, setDepAjoutees] = useState<Record<string, string[]>>({});
  const [refus, setRefus] = useState<string | null>(null);
  const [workbookDirty, setWorkbookDirty] = useState(false);
  const [manualRow, setManualRow] = useState<string | null | undefined>(undefined);
  const [pendingSaves, setPendingSaves] = useState(0);
  const [manualNavigation, setManualNavigation] = useState(0);
  const manualEditor = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!manualNavigation || !manualEditor.current) return;
    manualEditor.current.focus({ preventScroll: true });
    manualEditor.current.scrollIntoView({ block: "start", behavior: "auto" });
  }, [manualNavigation]);

  // ---------------------------------------------------------------------
  // Un enregistrement à la fois par ligne — et toujours la valeur la plus
  // fraîche
  // ---------------------------------------------------------------------
  //
  // Défaut relevé par la revue automatique de la PR #128 : en passant vite d'un
  // champ à l'autre, chaque `blur` lançait sa propre requête portant un
  // instantané COMPLET de la ligne. L'API remplaçant toutes les colonnes, une
  // requête partie avant mais arrivée après écrasait les valeurs plus récentes —
  // et l'écran affichait « Enregistré ». C'est précisément la perte silencieuse
  // que cette page promet d'éviter.
  //
  // Deux propriétés, ensemble :
  //   1. les envois d'une même ligne sont **enchaînés**, donc appliqués dans
  //      l'ordre d'édition ;
  //   2. chaque envoi lit les valeurs **au moment de partir**, pas au moment
  //      d'être demandé — un envoi en attente porte donc déjà la correction
  //      suivante, et ne peut pas ressusciter une valeur périmée.
  //
  // Les lignes restent indépendantes : deux employés différents s'enregistrent
  // toujours en parallèle.
  const valeursRef = useRef<Brouillon>({});
  const chaines = useRef<Map<string, Promise<void>>>(new Map());
  const editRevisions = useRef<Map<string, number>>(new Map());

  // Le miroir des saisies, lisible par un envoi déjà en file.
  valeursRef.current = local;

  const census = requete.data?.kind === "ready" ? requete.data.census : null;
  const lectureSeule = census?.status !== "draft";

  /**
   * La valeur affichée : la saisie locale si elle existe, sinon celle du serveur.
   *
   * L'ordre compte. Faire primer le serveur écraserait ce que la personne est en
   * train de taper au moment où une relecture arrive — c'est exactement la
   * « perte silencieuse » qu'on refuse.
   */
  const valeursDe = (rowId: string, serveur: Record<string, unknown>): Record<string, string> =>
    local[rowId] ?? enChaines(serveur);

  const participants = useMemo(() => {
    const duServeur = census?.participants ?? [];
    const vus = new Set(duServeur.map((p) => p.client_row_id));
    return [
      ...duServeur.map((p) => ({ rowId: p.client_row_id, serveur: p as Record<string, unknown> })),
      // Une fiche ajoutée à l'instant, pas encore enregistrée : elle existe à
      // l'écran avant d'exister en base, sinon le bouton « ajouter » ne ferait
      // rien de visible.
      ...ajoutees.filter((id) => !vus.has(id)).map((id) => ({ rowId: id, serveur: {} })),
    ];
  }, [census?.participants, ajoutees]);

  const dependantsDe = (participantRowId: string) => {
    const duServeur = (census?.dependents ?? []).filter(
      (d) => d.participant_client_row_id === participantRowId,
    );
    const vus = new Set(duServeur.map((d) => d.client_row_id));
    return [
      ...duServeur.map((d) => ({
        rowId: d.client_row_id,
        valeurs: valeursDe(d.client_row_id, d as Record<string, unknown>),
      })),
      ...(depAjoutees[participantRowId] ?? [])
        .filter((id) => !vus.has(id))
        .map((id) => ({ rowId: id, valeurs: local[id] ?? {} })),
    ];
  };

  const majChamp = (rowId: string, cle: string, v: string, serveur: Record<string, unknown>) => {
    editRevisions.current.set(rowId, (editRevisions.current.get(rowId) ?? 0) + 1);
    setEtats((p) => ({ ...p, [rowId]: "idle" }));
    setLocal((p) => ({ ...p, [rowId]: { ...valeursDe(rowId, serveur), [cle]: v } }));
  };

  /** Met l'envoi dans la file de SA ligne. Les autres lignes ne l'attendent pas. */
  function enregistrer(rowId: string, quoi: "participant" | "dependent", parent?: string): void {
    const precedent = chaines.current.get(rowId) ?? Promise.resolve();
    setPendingSaves((count) => count + 1);
    const suite = precedent
      .then(() => envoyer(rowId, quoi, parent))
      .finally(() => setPendingSaves((count) => count - 1));
    // La file ne doit pas se rompre sur un échec : la ligne doit rester
    // enregistrable après une coupure réseau.
    chaines.current.set(
      rowId,
      suite.catch(() => undefined),
    );
  }

  async function envoyer(rowId: string, quoi: "participant" | "dependent", parent?: string) {
    // Lues MAINTENANT, pas à la mise en file : un envoi qui attendait son tour
    // part avec la correction que la personne vient de taper.
    const valeurs = valeursRef.current[rowId];
    if (!valeurs) return;
    const sentRevision = editRevisions.current.get(rowId) ?? 0;
    setEtats((p) => ({ ...p, [rowId]: "saving" }));
    const corps =
      quoi === "participant"
        ? versLeFil(valeurs, CHAMPS_PARTICIPANT)
        : { ...versLeFil(valeurs, CHAMPS_DEPENDANT), participant_client_row_id: parent };
    const res = await (
      quoi === "participant"
        ? m.enregistrerParticipant.mutateAsync({ rowId, valeurs: corps })
        : m.enregistrerDependant.mutateAsync({ rowId, valeurs: corps })
    ).catch(() => null);
    if (!res) {
      setEtats((p) => ({ ...p, [rowId]: "error" }));
      return;
    }

    if (res.kind === "ok") {
      const unchanged = (editRevisions.current.get(rowId) ?? 0) === sentRevision;
      setEtats((p) => ({ ...p, [rowId]: unchanged ? "saved" : "idle" }));
      return;
    }
    setEtats((p) => ({ ...p, [rowId]: "error" }));
    if (res.kind === "unavailable") {
      // Le conseiller a peut-être verrouillé pendant la saisie. On relit : l'état
      // réel remplacera l'écran par « liste finalisée », plutôt qu'une erreur
      // opaque.
      void requete.refetch();
    } else if (res.code === "CENSUS_ROW_LIMIT_REACHED") {
      setRefus(t("census.public.limit_reached"));
    }
  }

  const manualBlocked =
    pendingSaves > 0 ||
    m.retirerParticipant.isPending ||
    m.retirerDependant.isPending ||
    Object.keys(local).some((id) => etats[id] !== "saved");
  const visibleManualRow =
    manualRow === undefined && participants.length === 1 ? participants[0]?.rowId : manualRow;

  function clearManualDrafts() {
    valeursRef.current = {};
    setLocal({});
    setEtats({});
    setAjoutees([]);
    setDepAjoutees({});
    chaines.current.clear();
    editRevisions.current.clear();
    setManualRow(null);
  }

  async function terminer() {
    if (workbookDirty || manualBlocked) return;
    setRefus(null);
    const res = await m.declarerTermine.mutateAsync();
    if (res.kind === "refused" && res.code === "CENSUS_EMPTY") {
      setRefus(t("census.public.empty_list"));
    } else if (res.kind === "unavailable") {
      void requete.refetch();
    }
  }

  // ---- états d'écran -------------------------------------------------------

  useEffect(() => {
    if (!workbookDirty && !manualBlocked) return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [workbookDirty, manualBlocked]);

  if (!census && (requete.isPaused || requete.data?.kind === "retryable") && !requete.isFetching) {
    return (
      <IntakeChrome>
        <div role="alert">
          <Panneau titre={t("census.public.retry_title")}>{t("census.public.retry_body")}</Panneau>
        </div>
        <button
          type="button"
          className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 mt-4 w-full touch-manipulation rounded-lg px-4 py-3 text-sm font-semibold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          onClick={() => void requete.refetch()}
        >
          {t("census.public.retry_action")}
        </button>
      </IntakeChrome>
    );
  }

  if (requete.isPending || (!census && requete.isFetching)) {
    return (
      <IntakeChrome>
        <p role="status" className="py-16 text-center text-sm text-slate-500">
          {t("census.public.loading")}
        </p>
      </IntakeChrome>
    );
  }

  if (!census) {
    return (
      <IntakeChrome>
        <Panneau titre={t("census.public.unavailable_title")}>
          {t("census.public.unavailable_body")}
        </Panneau>
      </IntakeChrome>
    );
  }

  if (census.status !== "draft") {
    const fini = census.status === "locked";
    return (
      <IntakeChrome>
        <Panneau titre={t(fini ? "census.public.locked_title" : "census.public.submitted_title")}>
          {t(fini ? "census.public.locked_body" : "census.public.submitted_body")}
        </Panneau>
        <p className="mt-4 text-center text-sm text-slate-500">
          {t("census.public.count", { count: census.participants.length })}
        </p>
      </IntakeChrome>
    );
  }

  return (
    <IntakeChrome layout="workspace">
      <h1 className="text-xl font-semibold text-slate-900">{t("census.public.title")}</h1>
      {census.company_name ? (
        <p className="mt-1 text-sm text-slate-600">
          {t("census.public.for_company", { company: census.company_name })}
        </p>
      ) : null}
      <p className="mt-3 text-sm text-slate-600">{t("census.public.intro")}</p>

      <ResumeManques issues={census.issues} />

      <CensusWorkbookPanel
        token={token}
        census={census}
        manualBlocked={manualBlocked}
        onDirtyChange={setWorkbookDirty}
        onApplied={clearManualDrafts}
        onEditEmployee={(rowId) => {
          setManualRow(rowId);
          setManualNavigation((request) => request + 1);
        }}
      />

      <div ref={manualEditor} tabIndex={-1} className="mt-5 space-y-4">
        {participants
          .filter((p) => p.rowId === visibleManualRow)
          .map((p) => (
            <FicheEmploye
              key={p.rowId}
              index={participants.findIndex((person) => person.rowId === p.rowId)}
              rowId={p.rowId}
              valeurs={valeursDe(p.rowId, p.serveur)}
              dependants={dependantsDe(p.rowId)}
              issues={census.issues}
              lectureSeule={lectureSeule || workbookDirty}
              etat={etats[p.rowId] ?? "idle"}
              onChampChange={(cle, v) => majChamp(p.rowId, cle, v, p.serveur)}
              onEnregistrer={() => enregistrer(p.rowId, "participant")}
              onRetirer={() => {
                if (pendingSaves || workbookDirty) return;
                const ids = new Set([
                  p.rowId,
                  ...dependantsDe(p.rowId).map((dependent) => dependent.rowId),
                ]);
                setLocal((old) =>
                  Object.fromEntries(Object.entries(old).filter(([id]) => !ids.has(id))),
                );
                setEtats((old) =>
                  Object.fromEntries(Object.entries(old).filter(([id]) => !ids.has(id))),
                );
                setAjoutees((a) => a.filter((x) => x !== p.rowId));
                setManualRow(null);
                if (census.participants.some((person) => person.client_row_id === p.rowId))
                  void m.retirerParticipant.mutateAsync(p.rowId);
              }}
              onDependantChange={(rowId, cle, v) =>
                majChamp(
                  rowId,
                  cle,
                  v,
                  (census.dependents.find((d) => d.client_row_id === rowId) ?? {}) as Record<
                    string,
                    unknown
                  >,
                )
              }
              onDependantEnregistrer={(rowId) => enregistrer(rowId, "dependent", p.rowId)}
              onDependantAjouter={() => {
                const id = nouvelId();
                setDepAjoutees((d) => ({ ...d, [p.rowId]: [...(d[p.rowId] ?? []), id] }));
                setLocal((l) => ({ ...l, [id]: {} }));
              }}
              onDependantRetirer={(rowId) => {
                if (pendingSaves || workbookDirty) return;
                setDepAjoutees((d) => ({
                  ...d,
                  [p.rowId]: (d[p.rowId] ?? []).filter((x) => x !== rowId),
                }));
                setLocal((old) =>
                  Object.fromEntries(Object.entries(old).filter(([id]) => id !== rowId)),
                );
                setEtats((old) =>
                  Object.fromEntries(Object.entries(old).filter(([id]) => id !== rowId)),
                );
                if (census.dependents.some((person) => person.client_row_id === rowId))
                  void m.retirerDependant.mutateAsync(rowId);
              }}
            />
          ))}
      </div>

      {visibleManualRow && (
        <button
          type="button"
          className="mt-3 rounded-lg border border-slate-300 px-3 py-2 text-sm disabled:opacity-50"
          disabled={pendingSaves > 0 || workbookDirty}
          onClick={clearManualDrafts}
        >
          {t(manualBlocked ? "census.workbook.discard_manual" : "census.workbook.close_details")}
        </button>
      )}

      <button
        type="button"
        disabled={
          workbookDirty || manualBlocked || participants.length >= census.limits.maxParticipants
        }
        onClick={() => {
          const id = nouvelId();
          setManualRow(id);
          setAjoutees((a) => [...a, id]);
          setLocal((l) => ({ ...l, [id]: {} }));
        }}
        className="focus-visible:ring-brand-500 mt-4 w-full touch-manipulation rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 hover:bg-white focus-visible:outline-none focus-visible:ring-2"
      >
        {t("census.public.add_employee")}
      </button>

      {refus ? (
        <p role="alert" className="mt-4 text-sm font-medium text-red-600">
          {refus}
        </p>
      ) : null}

      <div className="mt-8 border-t border-slate-200 pt-6">
        <p className="text-xs text-slate-500">{t("census.public.finish_hint")}</p>
        <button
          type="button"
          onClick={() => void terminer()}
          disabled={m.declarerTermine.isPending || workbookDirty || manualBlocked}
          className="focus-visible:ring-brand-500 bg-brand-600 hover:bg-brand-700 mt-2 w-full touch-manipulation rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-60"
        >
          {m.declarerTermine.isPending ? t("census.public.finishing") : t("census.public.finish")}
        </button>
      </div>
    </IntakeChrome>
  );
}

function Panneau({ titre, children }: { titre: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
      <h1 className="text-lg font-semibold text-slate-900">{titre}</h1>
      <p className="mt-2 text-sm text-slate-600">{children}</p>
    </div>
  );
}

/**
 * Le décompte des manques, tel que le serveur l'a calculé.
 *
 * Un nombre seul ne sert à rien — le détail vit sur chaque champ, à côté de
 * l'endroit où on le corrige. Ce résumé ne fait qu'annoncer qu'il y a du travail.
 */
function ResumeManques({ issues }: { issues: readonly CensusIssue[] }): JSX.Element | null {
  const { t } = useTranslation();
  if (issues.length === 0) return null;
  return (
    <p
      role="status"
      className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
    >
      {t("census.public.incomplete_title", { count: issues.length })}
    </p>
  );
}

export type { CensusParticipantRow };
